import {
  type LandingPartnerAdmin,
  type LandingPartnerFields,
  MAX_LANDING_PARTNERS,
} from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { useInvalidateLandingPartners, useLandingPartnersQuery } from '@/entities/landing/partner';
import {
  LandingPartnerCard,
  LandingPartnerFormModal,
  useAddLandingPartner,
  useDeleteLandingPartner,
  useMoveLandingPartner,
  useReorderLandingPartners,
  useReplaceLandingPartnerImage,
  useUpdateLandingPartner,
} from '@/features/landing/partners';
import { movedOrder, useReorderDrag } from '@/shared/lib';
import { Button, ContentCard, EmptyState } from '@/shared/ui';

type PartnerForm =
  | { readonly mode: 'create' }
  | { readonly mode: 'edit'; readonly item: LandingPartnerAdmin };

/**
 * HOME 함께한 기업 관리 — 로고 등록·기업명 수정·로고 교체·순서·삭제. 앞에서부터 HOME 로고 격자에 차례로 나온다.
 * 하나라도 등록하면 HOME 함께한 브랜드 자리에 이 로고가 나오고, 비어 있으면 포트폴리오 사례의 고객사 로고가 나온다.
 */
export const LandingPartnerManager = (): ReactElement => {
  const itemsQuery = useLandingPartnersQuery();
  const invalidate = useInvalidateLandingPartners();
  const addAction = useAddLandingPartner({ onSuccess: invalidate });
  const updateAction = useUpdateLandingPartner({ onSuccess: invalidate });
  const replaceAction = useReplaceLandingPartnerImage({ onSuccess: invalidate });
  const moveAction = useMoveLandingPartner({ onSuccess: invalidate });
  const deleteAction = useDeleteLandingPartner({ onSuccess: invalidate });
  const reorderAction = useReorderLandingPartners({ onSuccess: invalidate });
  const [form, setForm] = useState<PartnerForm | null>(null);

  const items = itemsQuery.data ?? [];
  const ids = items.map((item) => item.id);
  const isFull = items.length >= MAX_LANDING_PARTNERS;
  // 한 항목이라도 바뀌는 중이면 다른 카드의 버튼도 잠근다 — 히어로 구역과 같은 규칙.
  const isBusy =
    addAction.isPending ||
    updateAction.isPending ||
    moveAction.isPending ||
    deleteAction.isPending ||
    reorderAction.isPending ||
    replaceAction.pendingId !== null;
  const listError =
    replaceAction.error ??
    moveAction.error?.message ??
    reorderAction.error?.message ??
    deleteAction.error?.message ??
    null;
  // 손잡이를 끌어 다른 로고 앞·뒤에 놓으면 전체 순서를 한 번에 제출한다.
  const drag = useReorderDrag({
    scope: 'landing-partners',
    ids,
    disabled: isBusy,
    axis: 'x',
    onMove: (from, to) => {
      clearListErrors();
      reorderAction.reorderItems(movedOrder(ids, from, to));
    },
  });

  // 모달을 열고 닫을 때 지난 업로드·서버 오류를 지운다 — 새 모달에 옛 오류가 남지 않게.
  const changeForm = (next: PartnerForm | null): void => {
    addAction.reset();
    updateAction.reset();
    setForm(next);
  };

  const handleSubmit = async (fields: LandingPartnerFields, file: File | null): Promise<void> => {
    if (form === null) {
      return;
    }

    const isDone =
      form.mode === 'create'
        ? file !== null && (await addAction.addItem(file, fields))
        : await updateAction.updateItem(form.item.id, fields);

    if (isDone) {
      changeForm(null);
    }
  };

  // 로고 교체·이동·삭제는 오류 한 줄(listError)을 함께 쓴다. 새 요청을 보내기 전에 지난 오류를 모두 지운다.
  function clearListErrors(): void {
    replaceAction.reset();
    moveAction.reset();
    reorderAction.reset();
    deleteAction.reset();
  }

  const handleReplace = (id: string, file: File): void => {
    clearListErrors();
    void replaceAction.replaceImage(id, file);
  };

  const handleMove = (id: string, direction: 'UP' | 'DOWN'): void => {
    clearListErrors();
    moveAction.moveItem(id, direction);
  };

  const handleDelete = (item: LandingPartnerAdmin, order: number): void => {
    if (window.confirm(`기업 로고 ${order}번 "${item.name}"을 삭제하시겠습니까?`)) {
      clearListErrors();
      deleteAction.deleteItem(item.id);
    }
  };

  return (
    <ContentCard
      title="함께한 기업"
      description={`HOME 함께한 브랜드 로고 격자에 앞에서부터 차례로 나옵니다(한 줄 6개). 최대 ${MAX_LANDING_PARTNERS}개. 비어 있으면 포트폴리오 사례의 고객사 로고가 대신 나옵니다.`}
      actions={
        <>
          <span className="text-sm text-muted-foreground tabular-nums">
            {items.length}/{MAX_LANDING_PARTNERS}
          </span>
          <Button
            size="sm"
            disabled={isFull || isBusy}
            onClick={() => changeForm({ mode: 'create' })}
          >
            기업 로고 추가
          </Button>
        </>
      }
    >
      {listError !== null ? (
        <p role="alert" className="mb-4 text-sm text-destructive">
          {listError}
        </p>
      ) : null}

      {itemsQuery.isPending ? (
        <EmptyState>불러오는 중…</EmptyState>
      ) : itemsQuery.isError ? (
        <EmptyState isError>
          기업 로고를 불러오지 못했습니다.{' '}
          <button type="button" className="underline" onClick={() => void itemsQuery.refetch()}>
            다시 시도
          </button>
        </EmptyState>
      ) : items.length === 0 ? (
        <EmptyState>
          등록된 기업 로고가 없습니다. 추가하면 HOME 함께한 브랜드 자리에 나옵니다.
        </EmptyState>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-3 xl:grid-cols-6">
          {items.map((item, index) => (
            <LandingPartnerCard
              key={item.id}
              item={item}
              order={index + 1}
              isFirst={index === 0}
              isLast={index === items.length - 1}
              isBusy={isBusy}
              onMove={(direction) => handleMove(item.id, direction)}
              onEdit={() => changeForm({ mode: 'edit', item })}
              onReplace={(file) => handleReplace(item.id, file)}
              onDelete={() => handleDelete(item, index + 1)}
              handleProps={drag.handleProps(item.id)}
              rowProps={drag.rowProps(item.id)}
              dropEdge={
                drag.target?.id === item.id ? (drag.target.after ? 'after' : 'before') : undefined
              }
            />
          ))}
        </ul>
      )}

      {form !== null ? (
        <LandingPartnerFormModal
          mode={form.mode}
          initialFields={form.mode === 'edit' ? { name: form.item.name } : null}
          isPending={form.mode === 'create' ? addAction.isPending : updateAction.isPending}
          errorMessage={form.mode === 'create' ? addAction.error : updateAction.error}
          onSubmit={(fields, file) => void handleSubmit(fields, file)}
          onClose={() => changeForm(null)}
        />
      ) : null}
    </ContentCard>
  );
};
